import { api, MEMBER_API_SUB_URLS, V2 } from '@/api';
import { AxiosResponse } from 'axios';
import { Module } from 'vuex';
import {
  MemberARIsResponseModel,
  MemberHSAApplicantResponseModel,
  RootState,
  InterestRatesResponseModel,
} from '@/@types/member';

interface MemberState {
  ariDesignees: MemberARIsResponseModel[];
  hsaApplicant: MemberHSAApplicantResponseModel;
  interestRates: InterestRatesResponseModel;
}

const MEMBER_STATE: Module<MemberState, RootState> = {
  state: {
    ariDesignees: [],
    hsaApplicant: null,
    interestRates: null,
  },
  actions: {
    // UC-REST-045 - Get ARI Designees
    getARIDesignees(context): Promise<AxiosResponse> {
      return api
        .get(
          `${MEMBER_API_SUB_URLS.members}/${
            context.getters.user.memberId
          }/aris${V2}`, {
            params: {
              sortBy: '+lastName,+firstName',
              offset: '0',
              limit: '100',
            },
            includeInactive: false,
          })
        .then((ariDesignees) => {
          context.commit('setARIDesignees', ariDesignees.data);
          return ariDesignees.data;
        });
    },
    // UC-REST-046 - Update ARI Designee
    updateARIDesignee(context, options): Promise<AxiosResponse> {
      return api
        .put(
          `${MEMBER_API_SUB_URLS.aris}/${
            options.id
          }`,
          {
            firstName: options.firstName,
            lastName: options.lastName,
            startDate: options.startDate,
            endDate: options.endDate,
          },
        )
        .then((ariDesignee) => {
          return ariDesignee.data;
        });
    },
    // UC-REST-069 - Add ARI Designee
    addARIDesignee(context, options): Promise<AxiosResponse> {
      return api
        .post(
          `${MEMBER_API_SUB_URLS.members}/${
            context.getters.user.memberId
          }/aris${V2}`,
          {
            firstName: options.firstName,
            lastName: options.lastName,
            startDate: options.startDate,
            endDate: options.endDate,
          },
        )
        .then((ariDesignee) => {
          return ariDesignee.data;
        });
    },
    // UC-REST-051 - Add HSA Applicant
    addHSAApplicant(context, options): Promise<AxiosResponse> {
      const TOKEN = options.token;
      const HEADERS = {
        Authorization: TOKEN,
      };
      delete options.token;
      return api
        .post(
          `${MEMBER_API_SUB_URLS.members}/hsa-application${V2}`,
          options,
          {
            headers: HEADERS,
          },
        )
        .then((response) => {
          context.commit('setHSAApplicant', response.data);
          return response.data;
        });
    },
    // UC-REST-025 - Get Interest Rates
    async getInterestRates(context, planType): Promise<AxiosResponse> {
      const params = { planType, sortBy: '+lowLimit'};
      const RESPONSE: AxiosResponse = await api
        .get(
          `${MEMBER_API_SUB_URLS.interestRates}`, { params },
        );
      context.commit('setInterestRates', RESPONSE.data);
      return RESPONSE.data;
    },
  },
  mutations: {
    setARIDesignees(state: MemberState, data: MemberARIsResponseModel[]) {
      state.ariDesignees = data;
    },
    setHSAApplicant(state: MemberState, data: MemberHSAApplicantResponseModel) {
      state.hsaApplicant = data;
    },
    setInterestRates(state: MemberState, data: InterestRatesResponseModel) {
      state.interestRates = data;
    },
  },
  getters: {
    ariDesignees(state): MemberARIsResponseModel[] {
      return state.ariDesignees;
    },
    ariDesigneesQuantity(state): number {
      return state.ariDesignees.length;
    },
    hsaApplicant(state): MemberHSAApplicantResponseModel {
      return state.hsaApplicant;
    },
    interestRates(state): InterestRatesResponseModel {
      return state.interestRates;
    },
  },
};

export default MEMBER_STATE;
